import { DevShowProp } from "@/components/DevShowProp"
import { QueryRender } from "@/components/helpers/QueryRender"
import { useProcess } from "@/contexts/ProcessContext"
import { AbsenceRequest } from "@/entities/absenceRequest/absenceRequest"
import {
   useAbsenceRequest,
   useUpdateAbsenceRequest,
} from "@/entities/absenceRequest/useAbsenceRequest"
import { useCreateAttendanceEvent, useDeleteAttendanceEvent } from "@/entities/attendanceEvent/useAttendanceEvent"
import { useUser } from "@/entities/user/useUser"
import { useAuth } from "@/hooks/useAuth"
import { useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ConfirmDialog } from "../components/ui/confirmDialog"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { UserSmallProfile } from "@/components/ui/UserSmallProfile"
import { fr } from "date-fns/locale"
import { format } from "date-fns"
import { uploadFile } from "@/firebase/storage"
import {
   icon_outline_arrow_path,
   icon_outline_bars3_bottom_left,
   icon_outline_calendar,
   icon_outline_check,
   icon_outline_document,
   icon_outline_information_circle,
   icon_outline_x_mark,
} from "@/utils/svgIcons"
import { formatPeriodSmall, getStatusBadge } from "@/utils/commonAbsenceRequestUi"
import clsx from "clsx"
import { recurrencePeriodTranslations } from "@/types/recurrence"
import { runTransactionBatch } from "@/firestore/transactions"
import { createOneOrRecurringAttendanceEvents, deleteRecurringAttendanceEvents } from "@/controller/recurringAttendanceEvents"
import { useTimeMachine } from "@/contexts/TimeMachineContext"

export function AbsenceRequestContent({ id, variant }: { id: string; variant: "small" | "full" }) {
   const { now } = useTimeMachine()
   const { user } = useAuth()
   const { data: absenceRequest, ...query } = useAbsenceRequest({ id })
   const updateAbsenceRequest = useUpdateAbsenceRequest()
   const { startProcess } = useProcess()
   const { data: young } = useUser({ id: absenceRequest?.young_id })
   const { data: currentSchoolYear } = useCurrentSchoolYear()
   const [adminComment, setAdminComment] = useState("")
   const [showConfirmDialog, setShowConfirmDialog] = useState(false)
   const [showConfirmCancelDialog, setShowConfirmCancelDialog] = useState(false)
   const pendingActionRef = useRef<{
      status: AbsenceRequest["status"]
      comment: string
   } | null>(null)
   const createAttendanceEvent = useCreateAttendanceEvent()
   const deleteAttendanceEvent = useDeleteAttendanceEvent()

   const isAdmin = user?.role === "admin"
   const isYoung = user?.role === "young"
   const small = variant === "small"
   const full = variant === "full"

   const handleStatusUpdate = async (
      status: AbsenceRequest["status"],
      comment: string = absenceRequest?.admin_comment || "",
      makeAttendanceEventAsExpected: boolean = false,
   ) => {
      pendingActionRef.current = { status, comment }

      if (!comment && status === "rejected") {
         setShowConfirmDialog(true)
         return
      }

      if (status === "canceled") {
         setShowConfirmCancelDialog(true)
         return
      }

      await handleConfirmUpdate(makeAttendanceEventAsExpected)
   }

   const handleConfirmUpdate = async (makeAttendanceEventAsExpected: boolean = false) => {
      if (!pendingActionRef.current) return

      const { status, comment } = pendingActionRef.current
      setShowConfirmDialog(false)
      pendingActionRef.current = null

      await startProcess(async () => {
         if (!currentSchoolYear?.id) {
            throw new Error("No current school year set")
         }

         await updateAbsenceRequest(
            absenceRequest!.id,
            {
               status,
               admin_comment: comment,
            },
            {
               quickMode: true,
            },
         )
         // If validated, create the absence event
         if (status === "validated" || status === "validated_but_to_justify") {
            const hasJustificationDoc = !!absenceRequest!.justification_doc
            const justified = hasJustificationDoc
            const justification_doc = absenceRequest!.justification_doc || ""
            const justification_validation_status = hasJustificationDoc
               ? "validated"
               : "upload_pending"
            const reason = absenceRequest!.reason || ""

            await runTransactionBatch(async () => {
               const ids = await createOneOrRecurringAttendanceEvents({
                  is_recurring: absenceRequest!.is_recurring || false,
                  recurring_period: absenceRequest!.recurring_period!,
                  recurring_end_date: absenceRequest!.recurring_end_date!,
                  recurring_group: absenceRequest!.recurring_group!,
                  from: absenceRequest!.from,
                  to: absenceRequest!.to,
                  type: "absent",
                  justified,
                  young_id: absenceRequest!.young_id,
                  school_year_id: currentSchoolYear.id,
                  justification_validation_status,
                  justification_doc,
                  admin_comment: comment,
                  expected: makeAttendanceEventAsExpected,
                  reason,
                  createAttendanceEvent,
               })
               
               if (!absenceRequest?.is_recurring) {
                  await updateAbsenceRequest(
                     absenceRequest!.id,
                     {
                        attendance_event_id: ids[0],
                     },
                     {
                        quickMode: true,
                     },
                  )
               }
            })
         }

         if (status === "canceled") {
            if (absenceRequest?.is_recurring) {
               await runTransactionBatch(async () => {
                  await deleteRecurringAttendanceEvents({
                     recurring_group: absenceRequest!.recurring_group!,
                     minEventFrom: now,
                     deleteAttendanceEvent: (id) => deleteAttendanceEvent(id, { quickMode: true })
                  })
               })
            } else {
               await deleteAttendanceEvent(absenceRequest!.attendance_event_id!, { quickMode: true })
            }
         }
      })
   }

   const handleJustificationUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file || !absenceRequest) return

      startProcess(async () => {
         const fileUrl = await uploadFile(file, user!.id)
         await updateAbsenceRequest(
            absenceRequest.id,
            {
               justification_doc: fileUrl,
            },
            {
               quickMode: true,
            },
         )
      })
   }

   const canCancel = absenceRequest?.status === "validated" || absenceRequest?.status === "validated_but_to_justify"

   return (
      <QueryRender query={query} data={absenceRequest} emptyMessage="Demande d'absence non trouvée">
         {(absenceRequest) => (
            <>
               <DevShowProp label="ID" text={absenceRequest.id} />
               <div className={clsx("flex flex-col", small && "gap-2", full && "gap-4")}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                     {isAdmin && young && (
                        <UserSmallProfile
                           user={young}
                           size={full ? "lg" : "base"}
                           clickToGoToResume
                        />
                     )}
                     <div>{getStatusBadge(absenceRequest.status, full)}</div>
                  </div>

                  <div className="flex gap-4 items-center">
                     <span className="w-6">{icon_outline_calendar}</span>
                     {small && (
                        <div className="font-medium">
                           {formatPeriodSmall(absenceRequest.from, absenceRequest.to)}
                        </div>
                     )}
                     {full && (
                        <div className="flex flex-col">
                           <span>
                              Du {format(absenceRequest.from, "d MMMM yyyy HH:mm", { locale: fr })}
                           </span>
                           <span>
                              Au {format(absenceRequest.to, "d MMMM yyyy HH:mm", { locale: fr })}
                           </span>
                        </div>
                     )}
                  </div>

                  {absenceRequest.is_recurring && (
                     <div className="flex gap-4 items-center">
                        <span className="w-6">{icon_outline_arrow_path}</span>
                        <div className="flex flex-col">
                           <span>Se répète chaque {recurrencePeriodTranslations[absenceRequest.recurring_period!]}</span>
                           <span>jusqu'au {format(absenceRequest.recurring_end_date!, "d MMMM yyyy", { locale: fr })}</span>
                        </div>
                     </div>
                  )}

                  {absenceRequest.reason && (
                     <div className="flex gap-4 items-center">
                        <span className="w-6">{icon_outline_bars3_bottom_left}</span>
                        <span>{absenceRequest.reason}</span>
                     </div>
                  )}

                  {full && (
                     <div className="flex gap-4 items-center">
                        <span className="w-6">{icon_outline_document}</span>
                        {!absenceRequest.justification_doc && (
                           <span className="text-gray-500">Aucun justificatif</span>
                        )}
                        {absenceRequest.justification_doc && (
                           <a
                              href={absenceRequest.justification_doc}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm"
                           >
                              Télécharger le justificatif
                           </a>
                        )}
                     </div>
                  )}

                  {full && absenceRequest.admin_comment && (
                     <div role="alert" className="alert">
                        {icon_outline_information_circle}
                        <span>{absenceRequest.admin_comment}</span>
                     </div>
                  )}

                  {full &&
                     isYoung &&
                     user.id === absenceRequest.young_id &&
                     absenceRequest.status === "pending" && (
                        <div>
                           <div className="label">
                              <span className="label-text">Ajouter un justificatif</span>
                           </div>
                           <input
                              type="file"
                              className="file-input file-input-bordered file-input-primary w-full max-w-xs"
                              onChange={handleJustificationUpload}
                              accept="image/*,.pdf"
                           />
                        </div>
                     )}
               </div>

               {full && isAdmin && absenceRequest.status === "pending" && (
                  <>
                     <div className="divider mt-12">Admin</div>

                     <div className="form-control">
                        <input
                           type="text"
                           placeholder="Commentaire (optionnel)"
                           className="input input-bordered w-full"
                           value={adminComment}
                           onChange={(e) => setAdminComment(e.target.value)}
                        />
                     </div>

                     <div
                        className={clsx(
                           "mt-4 flex justify-center",
                           absenceRequest.justification_doc && "gap-8",
                           !absenceRequest.justification_doc && "gap-2 sm:gap-8",
                        )}
                     >
                        <button
                           className={clsx(
                              "btn btn-secondary",
                              absenceRequest.justification_doc && "w-32",
                           )}
                           onClick={() => handleStatusUpdate("rejected", adminComment)}
                        >
                           {icon_outline_x_mark}
                           Refuser
                        </button>

                        {absenceRequest.justification_doc ? (
                           <button
                              className="btn btn-primary w-32"
                              onClick={() => handleStatusUpdate("validated", adminComment)}
                           >
                              {icon_outline_check}
                              Valider
                           </button>
                        ) : (
                           <button
                              className="btn btn-primary"
                              onClick={() =>
                                 handleStatusUpdate("validated_but_to_justify", adminComment, true)
                              }
                           >
                              {icon_outline_check}
                              Acceptée mais à justifier
                           </button>
                        )}
                     </div>
                  </>
               )}

               {full && (
                  <div className="flex flex-col gap-4 items-center justify-center mt-4">
                     {isYoung && absenceRequest.status === "validated_but_to_justify" && (
                        <>
                           <div role="alert" className="alert alert-warning">
                              <svg
                                 xmlns="http://www.w3.org/2000/svg"
                                 className="h-6 w-6 shrink-0 stroke-current"
                                 fill="none"
                                 viewBox="0 0 24 24"
                              >
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                 />
                              </svg>
                              <span>Tu dois mettre ton justificatif sur {absenceRequest.is_recurring ? "chaque absence" : "l'absence"}</span>
                              {absenceRequest.attendance_event_id && (
                                 <Link
                                    to={`/jeune/absence/${absenceRequest.attendance_event_id}`}
                                    className="btn  gap-2"
                                 >
                                    Voir l'absence à justifier
                                 </Link>
                              )}
                           </div>
                        </>
                     )}

                     {isAdmin && absenceRequest.status === "validated_but_to_justify" && absenceRequest.attendance_event_id && (
                        <Link
                           to={`/admin/evenement/${absenceRequest.attendance_event_id}`}
                           className="btn btn-primary gap-2"
                        >
                           Voir l'absence concernée
                        </Link>
                     )}
                  </div>                  
               )}

               {full && canCancel && (
                  <>
                     <div className="divider mt-12"></div>
                     <div className="flex justify-center">
                        <button
                           className="btn btn-secondary w-32"
                           onClick={() => handleStatusUpdate("canceled")}
                        >
                           {icon_outline_x_mark}
                           Annuler
                        </button>
                     </div>
                  </>
               )}              

               {showConfirmDialog && (
                  <ConfirmDialog
                     message="Êtes-vous sûr de vouloir refuser cette demande d'absence sans commentaire ?"
                     onConfirm={() => handleConfirmUpdate()}
                     onCancel={() => {
                        setShowConfirmDialog(false)
                        pendingActionRef.current = null
                     }}
                  />
               )}

               {showConfirmCancelDialog && (
                  <ConfirmDialog
                     message="Êtes-vous sûr de vouloir annuler cette demande d'absence et les événements futurs liés ?"
                     onConfirm={() => handleConfirmUpdate()}
                     onCancel={() => {
                        setShowConfirmCancelDialog(false)
                        pendingActionRef.current = null
                     }}
                  />
               )}
            </>
         )}
      </QueryRender>
   )
}

export default function AbsenceRequestPage() {
   const { id } = useParams<{ id: string }>()
   if (!id) return null
   return (
      <div className="page-section">
         <AbsenceRequestContent id={id} variant="full" />
      </div>
   )
}
